import React, { useState, useRef, useEffect, useContext } from "react";
// import { useTable, usePagination } from "react-table";
import { useTable } from "react-table";
import {
  useInputSize,
  isAllowedNum,
  toPrecision,
  // isCompleteNumber,
  produceListOfExchangeableModels,
  hasProperty,
  getTextWithTooltip,
} from "../../../utils/helpers";
import addIcon from "../../../res/icons/add.png";
import deleteIcon from "../../../res/icons/delete.png";
import { HtmlInteractiveTooltip } from "../../commonComponents/HtmlTooltip";
import { GeneralContext } from "../../../context/GeneralContext";
import { DashboardContext } from "../../../context/DashboardContext";
import "./parameters.scss";
import ModelExchange from "./ModelExchange";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getModelById } from "../../leftSide/Models/modelLogic";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Modal from "react-modal";
import { getGroupColor } from "../../../theme/groupingColors";

// Create an editable cell renderer
const EditableCell = ({
  value: initialValue,
  row: { index },
  column: { id },
  updateMyData, // This is a custom function that we supplied to our table instance
  rec,
  handleCellClick,
  getCheckedValue,
  tableIndex,
  lineshape_id,
}) => {
  const { recordedErrorLog } = useContext(GeneralContext);

  const [value, setValue] = useState(initialValue);
  const valueRef = useRef(value);
  // const debounceTimeoutRef = useRef(null);

  const onChange = (e) => {
    try {
      let newValue = e.target.value;
      if (isAllowedNum(newValue)) {
        // const checkedValue = getCheckedValue(
        //   index,
        //   id,
        //   newValue,
        //   rec,
        //   tableIndex
        // );
        setValue(newValue);
        valueRef.current = newValue;

        // if (debounceTimeoutRef.current) {
        //   clearTimeout(debounceTimeoutRef.current);
        // }

        // debounceTimeoutRef.current = setTimeout(() => {
        //   if (isCompleteNumber(e.target.value)) {
        //     // onBlur(e);
        //   }
        //   debounceTimeoutRef.current = null;
        // }, 100); // Adjust the debounce time as needed
      }
    } catch (error) {
      recordedErrorLog("Table cell on change handler failure: ", error);
    }
  };

  const onBlur = (e) => {
    try {
      if (e !== undefined) {
        if (e.target.value === null) {
          updateMyData(index, id, "", rec, tableIndex, lineshape_id);
        } else if (!isNaN(e.target.value)) {
          const checkedValue = getCheckedValue(
            index,
            id,
            e.target.value,
            rec,
            tableIndex,
            lineshape_id
          );
          setValue(checkedValue);
          valueRef.current = checkedValue;
          updateMyData(
            index,
            id,
            Number(checkedValue),
            rec,
            tableIndex,
            lineshape_id
          );
        } else {
          updateMyData(
            index,
            id,
            e.target.value,
            rec,
            tableIndex,
            lineshape_id
          );
        }
      }
    } catch (error) {
      recordedErrorLog("Table cell onBlur handler failure: ", error);
    }
  };

  // If the initialValue is changed external, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleRightClick = (event) => {
    event.preventDefault();
  };

  const handleMouseDown = (event) => {
    if (event.button === 2) {
      // check if right mouse button is clicked
      event.preventDefault();
    }
  };

  const inputSize = useInputSize(value);

  return (
    <input
      className="tableCell"
      value={value != null ? value : ""}
      onChange={onChange}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onBlur(e);
        }
      }}
      onContextMenu={handleRightClick}
      onMouseDown={handleMouseDown}
      onClick={handleCellClick}
      style={{ width: `${inputSize + 20}px` }}
    />
  );
};

// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
  Cell: EditableCell,
};

function TooltipComponent({ title }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {isOpen ? (
        <div className="customTooltip">{title}</div>
      ) : (
        <div
          className="customTooltip clickable"
          onClick={() => setIsOpen(true)}
        >
          <HelpOutlineIcon />
        </div>
      )}
    </>
  );
}

// Be sure to pass our updateMyData and the skipPageReset option
function ParameterTable(props) {
  const { recordedErrorLog } = useContext(GeneralContext);
  try {
    const {
      columns,
      data,
      updateMyData,
      skipPageReset,
      getCellColor,
      getFontWeight,
      setFixed,
      getHeaderTitle,
      rec = false,
      lineshape_id = -1,
      handleAddLine,
      handleDuplicateLine,
      handleLineDelete,
      canDelete,
      handleTableCellClick,
      getCheckedValue,
      getColorForCell,
      tableIndex = null,
      sortTable,
      model,
      handleModelExchange,
      handleColumnAction,
      // handleExchangeToNewModel,
    } = props;

    const { recordedErrorLog } = useContext(GeneralContext);
    const { modelData, checkIfSubModTitle, uploadedFiles } =
      useContext(DashboardContext);
    const [exchangeModelList, setExchangeModelList] = useState([]);
    const [isExchangeOpen, setIsExchangeOpen] = useState(false);
    const [clickedSubModel, setClickedSubModel] = useState(null);
    const [recIndexForExchange, setRecIndexForExchange] = useState(-1);
    const [columnHeaderForExchange, setColumnHeaderForExchange] =
      useState(null);
    const tableRef = useRef(null);
    const [isColumnActionModalOpen, setIsColumnActionModalOpen] =
      useState(false);
    const [columnActionModalPosition, setColumnActionModalPosition] = useState({
      top: "0",
      left: "0",
    });
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [selectedColumnType, setSelectedColumnType] = useState(null);
    const [isInputModalOpen, setIsInputModalOpen] = useState(false);
    const [inputModalPosition, setInputModalPosition] = useState({
      top: "0",
      left: "0",
    });
    const [inputValue, setInputValue] = useState("");
    const [inputAction, setInputAction] = useState("");
    const [inputLabel, setInputLabel] = useState("");
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    const [groupModalPosition, setGroupModalPosition] = useState({
      top: "0",
      left: "0",
    });
    const [isFileSelectModalOpen, setIsFileSelectModalOpen] = useState(false);
    const [fileSelectModalPosition, setFileSelectModalPosition] = useState({
      top: "0",
      left: "0",
    });
    const [selectedCellForFileSelect, setSelectedCellForFileSelect] =
      useState(null);

    // For this example, we're using pagination to illustrate how to stop
    // the current page from resetting when our data changes
    // Otherwise, nothing is different here.
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
      useTable(
        {
          columns,
          data,
          defaultColumn,
          // use the skipPageReset option to disable page resetting temporarily
          autoResetPage: !skipPageReset,
          // updateMyData isn't part of the API, but
          // anything we put into these options will
          // automatically be available on the instance.
          // That way we can call this function from our
          // cell renderer!
          updateMyData,
        }
        // usePagination
      );

    const handleCellClick = (cell, event) => {
      // Check if this is a DataFile parameter
      const paramType = getParameterType(cell.column.id);
      if (paramType === "DataFile") {
        // Open file selection modal
        if (event) {
          const modalWidth = 400;
          const modalHeight = 500;
          const windowWidth = window.innerWidth;
          const windowHeight = window.innerHeight;

          let left = event.clientX || 0;
          let top = event.clientY || 0;

          // Adjust position if modal would go off screen
          if (left + modalWidth > windowWidth) {
            left = windowWidth - modalWidth - 10;
          }
          if (top + modalHeight > windowHeight) {
            top = windowHeight - modalHeight - 10;
          }
          if (left < 0) left = 10;
          if (top < 0) top = 10;

          setFileSelectModalPosition({
            top: `${top}px`,
            left: `${left}px`,
          });
        } else {
          setFileSelectModalPosition({
            top: "50%",
            left: "50%",
          });
        }
        setSelectedCellForFileSelect(cell);
        setIsFileSelectModalOpen(true);
      } else {
        handleTableCellClick(cell, rec, tableIndex, lineshape_id);
      }
    };

    const handleContextMenu = (cell, event) => {
      event.preventDefault();
      setFixed(cell, rec, tableIndex, lineshape_id);
    };

    const handleColHeaderClick = (column) => {
      if (rec && model.sort) {
        sortTable(column.id, lineshape_id);
      }
    };

    const handleColHeaderRightClick = (column, event) => {
      event.preventDefault();

      // Get the column type information
      let columnType = null;
      let fixed = true;
      if (
        rec &&
        lineshape_id === -1 &&
        model.recTemplate &&
        model.recTemplate.params
      ) {
        // For recurring parameters
        const param = model.recTemplate.params.find(
          (p) => p.name === column.id
        );
        if (param) {
          columnType = param.type;
          if (hasProperty(param, "fixed")) {
            fixed = param.fixed;
          }
        }
      } else if (
        rec &&
        lineshape_id !== -1 &&
        hasProperty(model, "lineshapeData") &&
        model.lineshapeData.length > 0
      ) {
        const lineshape = model.lineshapeData.find(
          (ls) => ls.lineshape_id === lineshape_id
        );
        if (
          lineshape &&
          hasProperty(lineshape, "lineTemplate") &&
          hasProperty(lineshape.lineTemplate, "params")
        ) {
          const param = lineshape.lineTemplate.params.find(
            (p) => p.name === column.id
          );
          if (param) {
            columnType = param.type;
            if (hasProperty(param, "fixed")) {
              fixed = param.fixed;
            }
          }
        }
      }

      if (columnType === null || columnType === "Model") {
        return;
      }
      if (columnType === "float") {
        columnType = fixed ? "float_fixed" : "float_unfixed";
      }

      // Set modal position based on click position
      const modalWidth = 300;
      const modalHeight = 200; // Approximate height
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let left = event.clientX;
      let top = event.clientY;

      // Adjust position if modal would go off screen
      if (left + modalWidth > windowWidth) {
        left = windowWidth - modalWidth - 10;
      }
      if (top + modalHeight > windowHeight) {
        top = windowHeight - modalHeight - 10;
      }

      setColumnActionModalPosition({
        top: `${top}px`,
        left: `${left}px`,
      });

      // Set selected column information
      setSelectedColumn(column);
      setSelectedColumnType(columnType);

      // Open the modal
      setIsColumnActionModalOpen(true);
    };

    // Check if the table contains any columns with submodels
    const hasSubmodelColumns = () => {
      if (lineshape_id !== -1) {
        // Lineshape tables don't have submodels
        return false;
      }

      // For recurring params check if any column is a Model type
      if (
        rec &&
        model.recTemplate &&
        model.recTemplate.params &&
        model.recTemplate.params.length > 0
      ) {
        return model.recTemplate.params.some((param) => param.type === "Model");
      }

      // For non-recurring, check based on column IDs
      for (const column of columns) {
        if (checkIfSubModTitle && checkIfSubModTitle(column.id)) {
          return true;
        }
      }

      return false;
    };

    const isScientificNotation = (val) => {
      return /^[+-]?\d+(\.\d+)?([eE][+-]?\d+)$/.test(val);
    };

    const produceValueForDisplay = (value) => {
      let val;
      if (value !== "") {
        if (isScientificNotation(value)) {
          val = value.toExponential(3);
          return val;
        } else if (value === 0) {
          return 0;
        } else if (value > 0 && `${toPrecision(value, 0)}`.length > 4) {
          val = toPrecision(value, 6).toExponential(3);
          return val;
        } else if (value < 0.0001 && value > 0) {
          val = value.toExponential(2);
          return val;
        } else if (value < -0.0001 && `${toPrecision(value, 0)}`.length > 5) {
          val = toPrecision(value, 6).toExponential(3);
          return val;
        } else if (value > -0.0001 && value < 0) {
          val = value.toExponential(3);
          return val;
        }
        val = toPrecision(value, 6);
        return val;
      } else {
        return "";
      }
    };

    // Helper function to get parameter type for a cell
    const getParameterType = (columnId) => {
      if (
        rec &&
        lineshape_id === -1 &&
        model.recTemplate &&
        model.recTemplate.params
      ) {
        // For recurring parameters
        const param = model.recTemplate.params.find((p) => p.name === columnId);
        if (param) {
          return param.type;
        }
      } else if (
        rec &&
        lineshape_id !== -1 &&
        hasProperty(model, "lineshapeData") &&
        model.lineshapeData.length > 0
      ) {
        // For lineshape recurring parameters
        const lineshape = model.lineshapeData.find(
          (ls) => ls.lineshape_id === lineshape_id
        );
        if (
          lineshape &&
          hasProperty(lineshape, "lineTemplate") &&
          hasProperty(lineshape.lineTemplate, "params")
        ) {
          const param = lineshape.lineTemplate.params.find(
            (p) => p.name === columnId
          );
          if (param) {
            return param.type;
          }
        }
      } else if (!rec && model.modelParams) {
        // For non-recurring parameters
        const param = model.modelParams.find(
          (p) => p.name === columnId && p.displaytable === tableIndex
        );
        if (param) {
          return param.type;
        }
      }
      return null;
    };

    // Helper function to get file name from uploadedFiles based on file ID
    const getFileNameForDataFile = (fileId) => {
      if (
        fileId === null ||
        fileId === undefined ||
        fileId === "" ||
        fileId < 0
      ) {
        return null;
      }
      const file = uploadedFiles.find((f) => f.ID === fileId);
      return file ? file.name : null;
    };

    const handleCheckboxClick = (rowIndex, columnId, currentValue) => {
      try {
        updateMyData(
          rowIndex,
          columnId,
          !currentValue,
          rec,
          tableIndex,
          lineshape_id
        );
      } catch (error) {
        recordedErrorLog("Checkbox click handler failure", error);
      }
    };

    const handleModelClick = (rowIndex, colHeader) => {
      try {
        // THIS WILL BECOME A PROBLEM IF THERE ARE NON REC AND REC SUB MODELS IN THE SAME MODEL !!!!
        // !!!!
        // !!!!
        let clickedSubModelLocal = null;
        let idOfModel = null;
        if (rec) {
          // Problem would only happen now for rec models in previously mentioned case, non rec sub models should be
          // much more robust.
          idOfModel = model.recParams[rowIndex].find(
            (param) => param.name === colHeader && param.type === "Model"
          ).FE_ID;
        } else {
          if (
            hasProperty(colHeader, "props") &&
            hasProperty(colHeader.props, "input")
          ) {
            colHeader = colHeader.props.input;
          }
          idOfModel = model.modelParams.find(
            (param) =>
              param.type === "Model" &&
              param.name === colHeader &&
              param.recuring === 0
          ).FE_ID;
        }
        if (idOfModel) {
          clickedSubModelLocal = getModelById(idOfModel, model.subModels);
          // Store recIndex and columnHeader for recurring parameters
        }
        if (clickedSubModelLocal) {
          clickedSubModelLocal = getModelById(idOfModel, model.subModels);
          // Store recIndex and columnHeader for recurring parameters
          setRecIndexForExchange(rec ? rowIndex : -1);
          setColumnHeaderForExchange(colHeader);
          setClickedSubModel(clickedSubModelLocal);

          const listOfOptions = produceListOfExchangeableModels(
            modelData,
            clickedSubModelLocal.FE_ID,
            clickedSubModelLocal.speqqleID
          );
          setExchangeModelList(listOfOptions);

          setIsExchangeOpen(true);
        } else {
          recordedErrorLog(
            "Model not found in model.subModels",
            null,
            true,
            "model linking failed, please try in the experiments tab",
            {
              idOfModel: idOfModel,
              model: model,
            }
          );
        }
      } catch (error) {
        recordedErrorLog(
          "Model click handler failure in parameter table",
          error
        );
      }
    };

    const handleCloseColumnActionModal = () => {
      setIsColumnActionModalOpen(false);
      setSelectedColumn(null);
      setSelectedColumnType(null);
    };

    const handleLocalColumnAction = (action) => {
      // Check if action requires input
      if (
        action === "change_value" ||
        action === "set_min_limit" ||
        action === "set_max_limit"
      ) {
        // Set up input modal
        setInputAction(action);
        setInputValue("");

        // Set input label based on action
        let label = "";
        switch (action) {
          case "change_value":
            label = "Enter new value:";
            break;
          case "set_min_limit":
            label = "Enter minimum limit:";
            break;
          case "set_max_limit":
            label = "Enter maximum limit:";
            break;
        }
        setInputLabel(label);

        // Position input modal near the action modal
        const inputModalWidth = 300;
        const inputModalHeight = 150; // Approximate height
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let inputLeft = parseInt(columnActionModalPosition.left) + 320;
        let inputTop = parseInt(columnActionModalPosition.top);

        // Adjust position if input modal would go off screen
        if (inputLeft + inputModalWidth > windowWidth) {
          inputLeft =
            parseInt(columnActionModalPosition.left) - inputModalWidth - 20;
        }
        if (inputTop + inputModalHeight > windowHeight) {
          inputTop = windowHeight - inputModalHeight - 10;
        }

        setInputModalPosition({
          top: `${inputTop}px`,
          left: `${inputLeft}px`,
        });

        // Close action modal and open input modal
        setIsColumnActionModalOpen(false);
        setIsInputModalOpen(true);
      } else if (action === "group") {
        // Set up group modal
        setInputAction(action);
        setInputValue("");
        setInputLabel("Select group to group with:");

        // Position group modal near the action modal
        const groupModalWidth = 550;
        const groupModalHeight = 470; // Updated height to accommodate title and scrollable grid
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let groupLeft = parseInt(columnActionModalPosition.left) + 320;
        let groupTop = parseInt(columnActionModalPosition.top);

        // Adjust position if group modal would go off screen
        if (groupLeft + groupModalWidth > windowWidth) {
          groupLeft =
            parseInt(columnActionModalPosition.left) - groupModalWidth - 20;
        }
        if (groupTop + groupModalHeight > windowHeight) {
          groupTop = windowHeight - groupModalHeight - 10;
        }

        setGroupModalPosition({
          top: `${groupTop}px`,
          left: `${groupLeft}px`,
        });

        // Close action modal and open group modal
        setIsColumnActionModalOpen(false);
        setIsGroupModalOpen(true);
      } else {
        // Handle other actions directly by calling the parent function
        if (handleColumnAction) {
          handleColumnAction(
            action,
            selectedColumn?.id,
            null,
            rec,
            tableIndex,
            lineshape_id
          );
        }
        handleCloseColumnActionModal();
      }
    };

    const handleInputSubmit = () => {
      // Call the parent function to handle the action
      if (handleColumnAction) {
        handleColumnAction(
          inputAction,
          selectedColumn?.id,
          inputValue,
          rec,
          tableIndex,
          lineshape_id
        );
      }

      // Close input modal
      setIsInputModalOpen(false);
      setInputValue("");
      setInputAction("");
      setInputLabel("");
    };

    const handleInputCancel = () => {
      setIsInputModalOpen(false);
      setInputValue("");
      setInputAction("");
      setInputLabel("");
    };

    const handleGroupModalClose = () => {
      setIsGroupModalOpen(false);
      setInputAction("");
      setInputLabel("");
    };

    const handleGroupSelect = (groupNumber) => {
      // Call the parent function to handle the group action
      if (handleColumnAction) {
        handleColumnAction(
          "group",
          selectedColumn?.id,
          groupNumber,
          rec,
          tableIndex,
          lineshape_id
        );
      }

      // Close group modal
      setIsGroupModalOpen(false);
      setInputAction("");
      setInputLabel("");
    };

    const handleInputKeyPress = (e) => {
      if (e.key === "Enter") {
        handleInputSubmit();
      } else if (e.key === "Escape") {
        handleInputCancel();
      }
    };

    // Focus input field when modal opens
    useEffect(() => {
      if (isInputModalOpen) {
        const inputElement = document.querySelector("#columnActionInput");
        if (inputElement) {
          inputElement.focus();
        }
      }
    }, [isInputModalOpen]);

    const getColumnActionOptions = () => {
      if (!selectedColumnType) return [];

      switch (selectedColumnType) {
        case "int":
          return [{ id: "change_value", label: "Set value" }];
        case "float_unfixed":
          return [
            { id: "change_value", label: "Set value" },
            { id: "fix", label: "Fix" },
            { id: "unfix", label: "Unfix" },
            { id: "set_min_limit", label: "Set min limit" },
            { id: "clear_min_limit", label: "Clear min limit" },
            { id: "set_max_limit", label: "Set max limit" },
            { id: "clear_max_limit", label: "Clear max limit" },
            { id: "group", label: "Group" },
            { id: "ungroup", label: "Ungroup" },
          ];
        case "float_fixed":
          return [{ id: "change_value", label: "Set value" }];
        case "Checkbox":
          return [
            { id: "change_on", label: "Set to on" },
            { id: "change_off", label: "Set to off" },
          ];
        default:
          return [];
      }
    };

    return (
      <>
        <div
          className={`tableContainer${props.title === "" ? " noTitle" : ""}`}
        >
          {props.title && <h3 className="tableTitle">{props.title}</h3>}
          <table {...getTableProps()} ref={tableRef}>
            <thead>
              {headerGroups.map((headerGroup, index) => {
                return (
                  <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      const headerTitle = getHeaderTitle(column, lineshape_id);
                      if (headerTitle !== "") {
                        const headerProps = column.getHeaderProps();
                        return (
                          <th
                            key={column.id}
                            {...headerProps}
                            onClick={() => handleColHeaderClick(column)}
                            onContextMenu={(event) =>
                              handleColHeaderRightClick(column, event)
                            }
                            style={{ textWrap: "nowrap" }}
                          >
                            <HtmlInteractiveTooltip
                              title={<TooltipComponent title={headerTitle} />}
                            >
                              <div>{column.render("Header")}</div>
                            </HtmlInteractiveTooltip>
                          </th>
                        );
                      } else {
                        return (
                          <th
                            key={column.id}
                            {...column.getHeaderProps()}
                            onContextMenu={(event) =>
                              handleColHeaderRightClick(column, event)
                            }
                          >
                            {column.render("Header")}
                          </th>
                        );
                      }
                    })}
                    {/* Add button in header row for recurring tables */}
                    {(rec || lineshape_id !== -1) && rows.length > 0 ? (
                      <td
                        id="buttonCell"
                        style={{
                          display: "flex",
                          visibility: "visible",
                          opacity: 0,
                          alignItems: "flex-end",
                        }}
                        className="headerButtonCell"
                      >
                        <div className="addContainer">
                          <img
                            src={addIcon}
                            alt="Add line at the top of the table"
                            className="addButton"
                            onClick={() =>
                              handleAddLine({ index: -1 }, lineshape_id)
                            }
                          />
                        </div>
                        <div className="spacer" />
                      </td>
                    ) : (
                      <></>
                    )}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                return (
                  <tr key={rowProps.key} {...rowProps}>
                    {row.cells.map((cell) => {
                      const cellProps = cell.getCellProps({
                        style: {
                          color: getCellColor(cell, rec, lineshape_id),
                          fontWeight: getFontWeight(cell, rec, lineshape_id),
                          backgroundColor: getColorForCell(
                            cell,
                            rec,
                            lineshape_id
                          ),
                        },
                        age: cell.row.original.age,
                        onContextMenu: (event) =>
                          handleContextMenu(cell, event),
                      });
                      return (
                        <td
                          key={cell.column.id + "|" + cell.row.index}
                          {...cellProps}
                          className="cellForTable"
                        >
                          {typeof cell.value === "boolean" ? (
                            <input
                              type="checkbox"
                              style={{ marginRight: "5px", marginLeft: "5px" }}
                              checked={cell.value}
                              onChange={() =>
                                handleCheckboxClick(
                                  cell.row.index,
                                  cell.column.id,
                                  cell.value
                                )
                              }
                            />
                          ) : checkIfSubModTitle(cell.column.Header) ||
                            (cell.column.Header.props &&
                              checkIfSubModTitle(
                                cell.column.Header.props.input
                              )) ? (
                            <div
                              className="modelCell"
                              onClick={() => {
                                handleModelClick(
                                  cell.row.index,
                                  //cell.column.id,
                                  cell.column.Header
                                );
                              }}
                            >
                              {typeof cell.value === "string"
                                ? getTextWithTooltip(cell.value, 25)
                                : cell.value}
                            </div>
                          ) : getParameterType(cell.column.id) ===
                            "DataFile" ? (
                            (() => {
                              const fileName = getFileNameForDataFile(
                                cell.value
                              );
                              return (
                                <div
                                  style={{
                                    padding: "2px 5px",
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCellClick(cell, e);
                                  }}
                                >
                                  {fileName ? (
                                    <span style={{ fontWeight: "normal" }}>
                                      {getTextWithTooltip(fileName, 25)}
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        fontWeight: "bold",
                                        color: "red",
                                      }}
                                    >
                                      None
                                    </span>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <EditableCell
                              value={produceValueForDisplay(cell.value)}
                              row={{ index: cell.row.index }}
                              column={{ id: cell.column.id }}
                              updateMyData={updateMyData}
                              rec={rec}
                              tableIndex={tableIndex}
                              handleCellClick={() => handleCellClick(cell)}
                              getCheckedValue={getCheckedValue}
                              lineshape_id={lineshape_id}
                            />
                          )}
                        </td>
                      );
                    })}
                    {(rec && canDelete()) || lineshape_id !== -1 ? (
                      <td
                        id="buttonCell"
                        className={hasSubmodelColumns() ? "no-duplicate" : ""}
                      >
                        <div className="addContainer">
                          <img
                            src={addIcon}
                            alt="Add another table line button"
                            className="addButton"
                            onClick={() => handleAddLine(row, lineshape_id)}
                            id="add-line-button"
                          />
                          {!hasSubmodelColumns() && (
                            <ContentCopyIcon
                              alt="Duplicate current table line button"
                              className="duplicateButton"
                              onClick={() =>
                                handleDuplicateLine(row, lineshape_id)
                              }
                            />
                          )}
                        </div>
                        <img
                          src={deleteIcon}
                          alt="Delete table line button"
                          className="deleteButton"
                          onClick={() => handleLineDelete(row, lineshape_id)}
                        />
                      </td>
                    ) : rec ? (
                      <td className="tableSpacerContainer">
                        <div className="spacer" />
                        <div className="spacer" />
                        <div className="spacer" />
                      </td>
                    ) : (
                      // <></>
                      <></>
                    )}
                    {rec && !canDelete() && lineshape_id === -1 ? (
                      <td
                        id="buttonCell"
                        className={hasSubmodelColumns() ? "no-duplicate" : ""}
                      >
                        <div className="addContainer">
                          <img
                            src={addIcon}
                            alt="Add another table line button"
                            className="addButton"
                            onClick={() => handleAddLine(row, lineshape_id)}
                            id="add-line-button"
                          />
                          {!hasSubmodelColumns() && (
                            <ContentCopyIcon
                              alt="Duplicate current table line button"
                              className="duplicateButton"
                              onClick={() =>
                                handleDuplicateLine(row, lineshape_id)
                              }
                            />
                          )}
                        </div>
                      </td>
                    ) : rec ? (
                      <td className="tableSpacerContainer">
                        <div className="spacer" />
                        <div className="spacer" />
                        <div className="spacer" />
                      </td>
                    ) : (
                      // <></>
                      <></>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {model.subModels.length > 0 && clickedSubModel !== null ? (
          <ModelExchange
            componentRef={tableRef}
            isOpen={isExchangeOpen}
            setIsOpen={setIsExchangeOpen}
            listOfOptions={exchangeModelList}
            model={clickedSubModel}
            handleModelExchange={handleModelExchange}
            // handleExchangeToNewModel={handleExchangeToNewModel}
            recIndex={recIndexForExchange}
            columnHeader={columnHeaderForExchange}
          />
        ) : (
          <></>
        )}
        <Modal
          isOpen={isColumnActionModalOpen}
          onRequestClose={handleCloseColumnActionModal}
          shouldCloseOnOverlayClick={true}
          contentLabel="Column Action Selection"
          appElement={tableRef.current}
          id="columnActionModal"
          style={{
            content: {
              width: "300px",
              height: "auto",
              position: "absolute",
              top: columnActionModalPosition.top,
              left: columnActionModalPosition.left,
              transform: "none",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "10px",
            },
            overlay: {
              backgroundColor: "transparent",
              zIndex: "8000",
            },
          }}
        >
          <div className="modalList">
            <div className="modalHeader">
              <h3>Apply to all current column entries</h3>
            </div>
            {getColumnActionOptions().map((option) => (
              <div key={option.id} className="modalListEntry">
                <div
                  className="modalListEntryName"
                  onClick={() => handleLocalColumnAction(option.id)}
                >
                  {option.label}
                </div>
              </div>
            ))}
          </div>
        </Modal>
        <Modal
          isOpen={isInputModalOpen}
          onRequestClose={handleInputCancel}
          shouldCloseOnOverlayClick={true}
          contentLabel="Input Modal"
          appElement={tableRef.current}
          id="inputModal"
          style={{
            content: {
              width: "300px",
              height: "auto",
              position: "absolute",
              top: inputModalPosition.top,
              left: inputModalPosition.left,
              transform: "none",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "10px",
            },
            overlay: {
              backgroundColor: "transparent",
              zIndex: "8000",
            },
          }}
        >
          <div className="inputModal">
            <div className="modalHeader">
              <h3>{inputLabel}</h3>
            </div>
            <input
              id="columnActionInput"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleInputKeyPress}
              placeholder="Enter value..."
              autoFocus
            />
            <div className="modalFooter">
              <button onClick={handleInputSubmit}>OK</button>
              <button onClick={handleInputCancel}>Cancel</button>
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={isFileSelectModalOpen}
          onRequestClose={() => setIsFileSelectModalOpen(false)}
          shouldCloseOnOverlayClick={true}
          contentLabel="Select File"
          appElement={tableRef.current}
          id="fileSelectModal"
          style={{
            content: {
              width: "400px",
              maxHeight: "500px",
              position: "absolute",
              top: fileSelectModalPosition.top,
              left: fileSelectModalPosition.left,
              transform: "none",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "10px",
              overflowY: "auto",
            },
            overlay: {
              backgroundColor: "transparent",
              zIndex: "8000",
            },
          }}
        >
          <div className="modalList">
            <div className="modalHeader">
              <h3>Select File</h3>
            </div>
            {uploadedFiles && uploadedFiles.length > 0 ? (
              uploadedFiles.map((file) => (
                <div
                  key={file.ID}
                  className="modalListEntry"
                  onClick={() => {
                    if (selectedCellForFileSelect) {
                      // Update the parameter value
                      updateMyData(
                        selectedCellForFileSelect.row.index,
                        selectedCellForFileSelect.column.id,
                        file.ID,
                        rec,
                        tableIndex,
                        lineshape_id
                      );
                    }
                    setIsFileSelectModalOpen(false);
                    setSelectedCellForFileSelect(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <div className="modalListEntryName">
                    {getTextWithTooltip(file.name, 50)}
                  </div>
                </div>
              ))
            ) : (
              <div className="modalListEntry">
                <div
                  className="modalListEntryName"
                  style={{ fontStyle: "italic", color: "#999" }}
                >
                  No files uploaded
                </div>
              </div>
            )}
          </div>
        </Modal>
        <Modal
          isOpen={isGroupModalOpen}
          onRequestClose={handleGroupModalClose}
          shouldCloseOnOverlayClick={true}
          contentLabel="Group Selection"
          appElement={tableRef.current}
          id="groupModal"
          style={{
            content: {
              width: "550px",
              height: "auto",
              maxHeight: "500px",
              position: "absolute",
              top: groupModalPosition.top,
              left: groupModalPosition.left,
              transform: "none",
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "10px",
            },
            overlay: {
              backgroundColor: "transparent",
              zIndex: "8000",
            },
          }}
        >
          <div className="modalList">
            <div className="modalHeader">
              <h3>Select Group</h3>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(10, 1fr)",
                gap: "5px",
                padding: "10px",
                maxHeight: "400px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {Array.from({ length: 100 }, (_, i) => {
                const groupNumber = i + 1;
                return (
                  <div
                    key={groupNumber}
                    className="modalListEntry"
                    style={{
                      backgroundColor: getGroupColor(groupNumber),
                      fontSize: "12px",
                      padding: "2px 0",
                      minHeight: "30px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "2px",
                      cursor: "pointer",
                      border: "1px solid #ccc",
                    }}
                    onClick={() => handleGroupSelect(groupNumber)}
                  >
                    <div
                      className="modalListEntryName"
                      style={{ color: "#000", fontWeight: "bold" }}
                    >
                      {groupNumber}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      </>
    );
  } catch (error) {
    recordedErrorLog("ERROR RENDERING THE PARAMETER TABLE!!: ", error);
    return <div>table render error</div>;
  }
}

export default ParameterTable;
